import { AnalyticsBrandDimensions, AnalyticsBrandEcommerceDimensions, AnalyticsComponentEvent } from 'idt-analytics-api/models';
import { EcommerceContext } from 'idt-ecommerce-api/context/context.models';
import { ProductsHelpers } from 'idt-ecommerce-api/catalog/products/products.helpers';
import { AnalyticsHelpersOysho } from './helpers/brand-analytics.helpers.oy';
import { pageTypes } from './helpers/brand-analytics-page-views.helpers.oy';
import { AnalyticsGTMSettings, isPageViewEvent } from 'analytics-api';

/** Métricas y dimensiones utilizadas para rellenar los valores envíados junto a los eventos a GTM */
export const AnalyticsBrandDimensionsOysho: AnalyticsBrandDimensions = {

  /** Tipo de aplicación desde la que se lanza el evento */
  1: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const platform = componentData?.device === 'mobile' ? 'web-mobile' : 'web-estandar';
    if (isPageViewEvent(settings)) {
      settings.context.platform = platform;
    }
    return settings;
  },

  /** Versión de la aplicación */
  2: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const version = componentData?.appVersion;
    if (isPageViewEvent(settings)) {
      settings.context.codeVersion = version;
    }
    return settings;
  },

  /** Versión de etiquetado de GTM */
  4: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    // TODO: Obtener versión de etiquetado de GTM. (GTM-XXXXX)
    return settings;
  },

  /** Clase usuario (Sesión) */
  7: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const profileStatus = componentData?.user?.profileStatus;
    if (isPageViewEvent(settings)) {
      settings.user.profileStatus = profileStatus;
    } else {
      settings.customData.user.profileStatus = profileStatus;
    }
    return settings;
  },

  /** Indica si el usuario tiene cuenta o no, puede tener valor 'anonimo' o 'registrado' */
  10: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    // TODO: Tipo de usuario, registrado o anonimo.
    const userType = 'anonimo';
    if (isPageViewEvent(settings)) {
      settings.user.userType = userType;
    } else {
      settings.customData.user.userType = userType;
    }
    return settings;
  },

  /** Identificador de la tienda Se forma con el identificador de la cadena concatenado con guion bajo con el iso de país */
  11: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const storeId = `${context?.config.brandSettings.brandName.toUpperCase()}_${context?.store.countryCode || 'ZZ'}`;
    if (isPageViewEvent(settings)) {
      settings.context.storeId = storeId;
    }
    return settings;
  },

  /** (Código ISO) Idioma de consumo del contenido, se forma con el código del idioma concatenado con guion bajo con el iso de país  */
  12: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const language = `${context?.currentLanguage?.code}_${context?.store.countryCode}`;
    if (isPageViewEvent(settings)) {
      settings.context.language = language;
    }
    return settings;
  },

  /** Número de tiendas cercanas encontradas en la búsqueda del usuario */
  13: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const numStores = componentData?.numStores?.toString();
    if (isPageViewEvent(settings)) {
      settings.store.storeFound = numStores;
    }
    return settings;
  },

  /** Referencia de la temporada a la que pertenece un producto */
  15: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const season = componentData?.products?.length ? componentData.products[0].fullReference?.split('-')[1] : undefined;
    if (isPageViewEvent(settings)) {
      settings.category.season = season;
    } else {
      settings.customData.category.season = season;
    }
    return settings;
  },

  /** Categoría padre a la que pertenece la página  */
  16: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const category = AnalyticsHelpersOysho.getCategory('category', context, componentData);
    if (isPageViewEvent(settings)) {
      settings.category.category = category;
    } else {
      settings.customData.category.category = category;
    }
    return settings;
  },

  /** Segundo nivel de categorización dentro del que está la página  */
  17: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const category = AnalyticsHelpersOysho.getCategory('subcategory', context, componentData);
    if (isPageViewEvent(settings)) {
      settings.category.subcategory = category;
    } else {
      settings.customData.category.subcategory = category;
    }
    return settings;
  },

  /** Número de productos por fila que se muestran en el catálogo */
  19: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const viewType = `cat_${componentData?.numColumns}`;
    if (isPageViewEvent(settings)) {
      settings.category.viewType = viewType;
    } else {
      settings.customData.category.viewType = viewType;
    }
    return settings;
  },

  /** Identificador alfanumérico de un producto. Siempre sin color y sin talla */
  20: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const partnumber = componentData?.products?.length ? componentData.products[0].fullReference : componentData?.partnumber;
    if (isPageViewEvent(settings)) {
      settings.product.reference = partnumber;
    } else {
      settings.customData.product.reference = partnumber;
    }
    return settings;
  },

  /** Indica si existe video en la ficha de producto (si|no) */
  22: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const colorId = componentData?.products?.length ? componentData.products[0].color : undefined;
    const selectedColor = componentData?.products?.length ? componentData.products[0].colors.find((color) => color.id === colorId) : undefined;
    const images = selectedColor ? ProductsHelpers.imagesForColor(selectedColor, 'product_page') : undefined;
    const video = images && images.findIndex((media) => media?.contentType?.type === 'video') !== -1 ? 'si' : 'no';
    if (isPageViewEvent(settings)) {
      settings.photo.video = video;
    }
    return settings;
  },

  /** Número de resultados de búsqueda interna */
  24: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const numResults = componentData?.numProducts?.toString();
    if (isPageViewEvent(settings)) {
      settings.searchEngine.searchResultNr = numResults;
    } else {
      settings.customData.photo.linkType = numResults;
    }
    return settings;
  },

  /** Número de productos en la cesta */
  31: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const productsOnCart = componentData?.numProducts?.toString();
    if (isPageViewEvent(settings)) {
      settings.cart.cartNumberItems = productsOnCart;
    } else {
      settings.customData.cart.cartNumberItems = productsOnCart;
    }
    return settings;
  },

  /** Número de productos no disponibles en la cesta */
  32: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const notAvailableProducts = componentData?.notAvailableProducts?.toString();
    if (isPageViewEvent(settings)) {
      settings.cart.cartNumberItemsNoStock = notAvailableProducts;
    } else {
      settings.customData.cart.cartNumberItemsNoStock = notAvailableProducts;
    }
    return settings;
  },

  /** Tiene datos de pago guardados. Valores: si | no */
  38: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const quickBuy = componentData?.user?.quickBuy;
    if (isPageViewEvent(settings)) {
      settings.user.quickBuy = quickBuy;
    } else {
      settings.customData.user.quickBuy = quickBuy;
    }
    return settings;
  },

  /** Indica si el usuario utilizó un click en texto o foto para acceder al contenido */
  46: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const elementType = componentData?.elementType;
    if (isPageViewEvent(settings)) {
      settings.photo.linkType = elementType;
    } else {
      settings.customData.photo.linkType = elementType;
    }
    return settings;
  },

  /** Indica la clase de foto representada por un nº entero */
  47: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    let typePhoto = componentData?.products?.length ? componentData.products[0].image?.clazz.id.toString() : undefined;
    if (!typePhoto && componentData?.products?.length) {
      const colorId = componentData.products[0].color;
      const selectedColor = componentData.products[0].colors.find((color) => color.id === colorId);
      const images = selectedColor ? ProductsHelpers.imagesForColor(
        selectedColor,
        (componentData.products[0].location || 'category_page')
      ) : undefined;
      typePhoto = images?.length ? images[0].clazz.id.toString() : undefined;
    }
    if (isPageViewEvent(settings)) {
      settings.photo.typePhoto = typePhoto;
    } else {
      settings.customData.photo.typePhoto = typePhoto;
    }
    return settings;
  },

  /** Identificador de 3 cifras del color del producto */
  48: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const color = componentData?.products?.length ? (componentData.products[0].color || componentData.products[0].colors[0].id) : undefined;
    if (isPageViewEvent(settings)) {
      settings.product.color = color;
    } else {
      settings.customData.product.color = color;
    }
    return settings;
  },

  /** Término utilizado en la búsqueda */
  49: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const searchTerm = componentData?.buttonText;
    if (isPageViewEvent(settings)) {
      settings.searchEngine.searchTerm = searchTerm;
    } else {
      settings.customData.searchEngine.searchTerm = searchTerm;
    }
    return settings;
  },

   /** Contenido de la cesta */
   51: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const cartProducts = componentData?.products?.map(el => ({ quantity: el.quantity?.toString(), reference: el.fullReference }));
    if (isPageViewEvent(settings)) {
      settings.cart.cartProducts = cartProducts;
    } else {
      settings.customData.cart.cartProducts = cartProducts;
    }
    return settings;
  },

  /** Tipo de cliente. Valores: particular | empresa */
  55: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const clientType = componentData?.user?.clientType;
    if (isPageViewEvent(settings)) {
      settings.user.clientType = clientType;
    } else {
      settings.customData.user.clientType = clientType;
    }
    return settings;
  },

  /** Identificador de 2 o 3 cifras de la talla del producto */
  58: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const sizeCode = componentData?.products?.length ? componentData.products[0].sizeCode : undefined;
    if (isPageViewEvent(settings)) {
      settings.product.talla = sizeCode;
    } else {
      settings.customData.product.talla = sizeCode;
    }
    return settings;
  },

  /** Comprador.
   * Indica si el usuario es o no comprador.
   * Si no te logueas enviará siempre "no" a no ser que compre como invitado.
   * Si te logueas comprobará si fuiste comprador en otra sesión.
   * Valores: si | no
   */
  59: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const isBuyer = componentData?.user?.isBuyer;
    if (isPageViewEvent(settings)) {
      settings.user.isBuyer = isBuyer;
    } else {
      settings.customData.user.isBuyer = isBuyer;
    }
    return settings;
  },

  /** URL física de la página */
  63: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const url = window.location.pathname;
    if (isPageViewEvent(settings)) {
      settings.page.pageURL = url;
    }
    return settings;
  },

  /** Número de items en parrilla */
  64: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const items = componentData?.numProducts?.toString() || componentData?.products?.length.toString();
    if (isPageViewEvent(settings)) {
      settings.category.itemsNumber = items;
    } else {
      settings.customData.category.itemsNumber = items;
    }
    return settings;
  },

  /** Referencia identificativa de bundle */
  65: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const bundleReference = componentData?.products?.length ? componentData.products[0].fullReference : undefined;
    if (isPageViewEvent(settings)) {
      settings.look.bundleReference = bundleReference;
    } else {
      settings.customData.look.bundleReference = bundleReference;
    }
    return settings;
  },

  /** Sección a la que pertenece el contenido (catalogo, home, etc) */
  67: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    if (isPageViewEvent(settings) && componentData?.slug) {
      settings.page.section = pageTypes.get(componentData.slug)?.section;
    }
    return settings;
  },

  /** ID de usuario
   * Ejemplo: 101192783630
   */
   68: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const userId = componentData?.user?.userId;
    if (isPageViewEvent(settings)) {
      settings.user.userId = userId;
    } else {
      settings.customData.user.userId = userId;
    }
    return settings;
  },

  /** Tipo de producto (temporada|saldo) */
  70: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    let productType: string | undefined;
    if (componentData?.products?.length) {
      const product = componentData.products[0];
      const selectedColor = product.colors.find((color) => color.id === product.color);
      const selectedSize = selectedColor?.sizes?.find((size) => size.masterSizeId === product.sizeCode);
      const oldPrice = selectedSize?.priceInfo?.oldPrice ? selectedSize?.priceInfo?.oldPrice : product.priceInfo?.oldPrice;
      productType = oldPrice ? 'saldo' : 'temporada';
    }
    if (isPageViewEvent(settings)) {
      settings.product.productState = productType;
    } else {
      settings.customData.product.productState = productType;
    }
    return settings;
  },

  /** Penúltimo nivel de categorización de la página. Se debe especificar en inglés */
  71: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const category = AnalyticsHelpersOysho.getCategory('subsubcategory', context, componentData);
    if (isPageViewEvent(settings)) {
      settings.category.subsubcategory = category;
    } else {
      settings.customData.category.subsubcategory = category;
    }
    return settings;
  },

  /** Consumidor del producto (en Oysho siempre 'woman') */
  72: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const consumer = 'woman';
    if (isPageViewEvent(settings)) {
      settings.product.consumerProduct = consumer;
    } else {
      settings.customData.product.consumerProduct = consumer;
    }
    return settings;
  },

  /** Estado del usuario (no_logueado|logueado) */
  73(settings: AnalyticsGTMSettings, context?: EcommerceContext): AnalyticsGTMSettings {
    // TODO: Obtener este dato una vez esté implementado e integrado el servicio de login.
    if (isPageViewEvent(settings)) {
      settings.user.isLogged = 'no_logueado';
    }
    return settings;
  },

  /** Disponibilidad en stock (si|no)  */
  74: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    let stock: string | undefined;
    if (componentData?.products?.length) {
      const product = componentData.products[0];
      const selectedColor = product.colors.find((color) => color.id === product.color);
      const selectedSize = selectedColor?.sizes?.find((size) => size.masterSizeId === product.sizeCode);
      stock = selectedSize?.availability === 'in_stock' ? 'si' : 'no';
    }
    if (isPageViewEvent(settings)) {
      settings.product.stock = stock;
    } else {
      settings.customData.product.stock = stock;
    }
    return settings;
  },

  /** Código postal de una ubicación/tienda */
  75: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const zipCode = componentData?.zipCode;
    if (isPageViewEvent(settings)) {
      settings.store.zipCode = zipCode;
    } else {
      settings.customData.store.zipCode = zipCode;
    }
    return settings;
  },

  /** Indica el filtro aplicado y los valores escogidos */
  78: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    if (isPageViewEvent(settings)) {
      settings.category.filter = componentData?.selectedFilter;
    } else {
      settings.customData.category.filter = componentData?.selectedFilter;
    }
    return settings;
  },

  /** Nombre identificativo del video */
  80: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const videoName = undefined;
    if (isPageViewEvent(settings)) {
      settings.look.videoName = videoName;
    } else {
      settings.customData.look.videoName = videoName;
    }
    return settings;
  },

  /** Identificador numérico de tienda física */
  83: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const storeId = componentData?.physicalStore?.id;
    if (isPageViewEvent(settings)) {
      settings.store.storeId = storeId;
    } else {
      settings.customData.store.storeId = storeId;
    }
    return settings;
  },

  /** Indica la edad del usuario
   * Ejemplo: 27
   */
  88: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const age = componentData?.user?.age;
    if (isPageViewEvent(settings)) {
      settings.user.age = age;
    } else {
      settings.customData.user.age = age;
    }
    return settings;
  },

  /** Indica el sexo del usuario
   * Valores: hombre | mujer
   */
  89: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const sex = componentData?.user?.sex;
    if (isPageViewEvent(settings)) {
      settings.user.sex = sex;
    } else {
      settings.customData.user.sex = sex;
    }
    return settings;
  },

  /** Tipo de contenido al que pertenece la página (Ej: ficha_producto) */
  90: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    if (isPageViewEvent(settings) && componentData?.slug) {
      settings.page.pageType = pageTypes.get(componentData.slug)?.pageType;
    }
    return settings;
  },

  /** Indica si la página/app tiene habilitada la compra online */
  92: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    if (isPageViewEvent(settings)) {
      settings.context.isOpenStore = context?.store?.isOpenForSale ? 'abierta' : 'cerrada';
    }
    return settings;
  },

  /** Indica si un producto es exclusivo de venta online */
  93: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const online = undefined;
    if (isPageViewEvent(settings)) {
      settings.product.onlineOnly = online;
    } else {
      settings.customData.product.onlineOnly = online;
    }
    return settings;
  },

  /** Indica si se trata de un producto de ropa(0) o calzado(1) */
  98: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const productClass = componentData?.products?.length ? componentData.products[0].productType : undefined;
    const classId = productClass?.toLowerCase() === 'clothing' ? '0' : '1';
    if (isPageViewEvent(settings)) {
      settings.product.claseProducto = classId;
    } else {
      settings.customData.product.claseProducto = classId;
    }
    return settings;
  },

  /** Numero que identifica el/la modelo que aparece llevando la prenda/calzado */
  99: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const modelId = undefined;
    if (isPageViewEvent(settings)) {
      settings.product.model = modelId;
    } else {
      settings.customData.product.model = modelId;
    }
    return settings;
  },

  /** Indica el tipo de luz utilizado en las fotos de producto */
  100: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const lightType = undefined;
    if (isPageViewEvent(settings)) {
      settings.photo.lightType = lightType;
    } else {
      settings.customData.photo.lightType = lightType;
    }
    return settings;
  },

  /** Indica el tipo de fondo utilizado en las fotos */
  101: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const backgroundType = undefined;
    if (isPageViewEvent(settings)) {
      settings.photo.background = backgroundType;
    } else {
      settings.customData.photo.background = backgroundType;
    }
    return settings;
  },

  /** Sección principal a la que pertenece el producto */
  108: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const section = '1_woman';
    if (isPageViewEvent(settings)) {
      settings.product.section = section;
    } else {
      settings.customData.product.section = section;
    }
    return settings;
  },

  /** Familia a la que pertenece el producto */
  110: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const product = componentData?.products?.length ? componentData.products[0] : undefined;
    const family = product ? `${product.familyCode}_${product.familyName}` : undefined;
    if (isPageViewEvent(settings)) {
      settings.product.family = family;
    } else {
      settings.customData.product.family = family;
    }
    return settings;
  },

  /** Subfamilia a la que pertenece el producto */
  112: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const product = componentData?.products?.length ? componentData.products[0] : undefined;
    const subfamily = product ? `${product.subFamilyCode}_${product.subFamilyName}` : undefined;
    if (isPageViewEvent(settings)) {
      settings.product.subfamily = subfamily;
    } else {
      settings.customData.product.subfamily = subfamily;
    }
    return settings;
  },

  /** Indica el tipo de encuadre utilizado en la foto */
  117: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const frame = undefined;
    if (isPageViewEvent(settings)) {
      settings.photo.frame = frame;
    } else {
      settings.customData.photo.frame = frame;
    }
    return settings;
  },

  /** Tipo de login: Indica el tipo de login que ha hecho el usuario. Puede tener los valores: login_estandar|login_facebook */
  136: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const loginType = componentData?.user?.loginType;
    if (isPageViewEvent(settings)) {
      settings.user.loginType = loginType;
    } else {
      settings.customData.user.loginType = loginType;
    }
    return settings;
  },

  /** Indica el tipo de producto cross que aparece en los distintos carruseles (Ej: productos_relacionados) */
  139: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const crossSellingType = componentData?.crossType;
    if (isPageViewEvent(settings)) {
      settings.category.crossType = crossSellingType;
    } else {
      settings.customData.category.crossType = crossSellingType;
    }
    return settings;
  },

  /** Indica en el tipo de búsqueda que ha realizado el usuario, si ha sido manual o
     *  por el contrario, ha usado los bloques sugeridos en el buscador (Ej: manual)
     */
  178: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const searchType = componentData?.searchType;
    if (isPageViewEvent(settings)) {
      settings.searchEngine.searchType = searchType;
    } else {
      settings.customData.searchEngine.searchType = searchType;
    }
    return settings;
  },

  /** Último nivel de categorización Se debe especificar en inglés */
  198: (settings: AnalyticsGTMSettings, context?: EcommerceContext, componentData?: AnalyticsComponentEvent): AnalyticsGTMSettings => {
    const category = AnalyticsHelpersOysho.getCategory('subsubsubcategory', context, componentData);
    if (isPageViewEvent(settings)) {
      settings.category.subsubsubcategory = category;
    } else {
      settings.customData.category.subsubsubcategory = category;
    }
    return settings;
  }
};

/** Dimensiones exclusivas de los objetos de ecommerce */
export const AnalyticsBrandEcommerceDimensionsOysho: AnalyticsBrandEcommerceDimensions = {

  /** Indica si un producto puede ser personalizado por el usuario */
  29: (componentData: AnalyticsComponentEvent, context?: EcommerceContext): string | undefined => {
    return undefined;
  },

  /** Indica si el producto contiene un video (si/no) */
  69: (componentData: AnalyticsComponentEvent, context?: EcommerceContext): string | undefined => {
    return undefined;
  },

  /** Indica si el producto se ha comprado utilizando el recomendador de tallas (no_se_usa|hizo_caso|no_hizo_caso) */
  91: (componentData: AnalyticsComponentEvent, context?: EcommerceContext): string | undefined => {
    return undefined;
  },

  /** Identificador de 3 cifras del color del producto. */
  96: (componentData: AnalyticsComponentEvent, context?: EcommerceContext): string | undefined => {
    if (isDefined(componentData.products) && isDefined(componentData.productIndex)) {
      return componentData.products[componentData.productIndex].color || componentData.products[componentData.productIndex]?.colors[0]?.id;
    }
    return undefined;
  },

  /** Identificador de 2 o 3 cifras de la talla del producto */
  97: (componentData: AnalyticsComponentEvent, context?: EcommerceContext): string | undefined => {
    if (isDefined(componentData.products) && isDefined(componentData.productIndex)) {
      const product = componentData.products[componentData.productIndex];
      return product?.sizeCode;
    }
    return undefined;
  },

  /** Número que identifica el/la modelo que aparece llevando la prenda/calzado */
  107: (componentData: AnalyticsComponentEvent, context?: EcommerceContext): string | undefined => {
    return undefined;
  },

  /** Sección principal a la que pertenece el producto (Ej: 1_woman) */
  109(componentData: AnalyticsComponentEvent, context?: EcommerceContext): string {
    return '1_woman';
  },

  /** Familia a la que pertenece el producto familyId_familyName (Ej: 11_shirts) */
  111: (componentData: AnalyticsComponentEvent, context?: EcommerceContext): string | undefined => {
    if (isDefined(componentData.products) && isDefined(componentData.productIndex)) {
      const product = componentData.products[componentData.productIndex];
      return `${product.familyCode}_${product.familyName}`;
    }
    return undefined;
  },

  /** Subfamilia a la que pertenece el producto subFamilyId_subFamilyName (Ej: 111_short_sleeve_shirts) */
  113: (componentData: AnalyticsComponentEvent, context?: EcommerceContext): string | undefined => {
    if (isDefined(componentData.products) && isDefined(componentData.productIndex)) {
      const product = componentData.products[componentData.productIndex];
      return `${product.subFamilyCode}_${product.subFamilyName}`;
    }
    return undefined;
  },

  /** Indica el tipo de luz utilizado en las fotos de producto (Ej: fría) */
  114: (componentData: AnalyticsComponentEvent, context?: EcommerceContext): string | undefined => {
    return undefined;
  },

  /** Indica el tipo de fondo utilizado en las fotos (Ej: liso) */
  115: (componentData: AnalyticsComponentEvent, context?: EcommerceContext): string | undefined => {
    return undefined;
  },

  /** Indica la clase de foto representada por un nº entero */
  116: (componentData: AnalyticsComponentEvent, context?: EcommerceContext): string | undefined => {
    return undefined;
  },

  /** Indica el tipo de encuadre utilizado en la foto. (Ej: 2) */
  118: (componentData: AnalyticsComponentEvent, context?: EcommerceContext): string | undefined => {
    return undefined;
  },

  /** Indica si un producto añadido a la cesta tiene alguna alerta relacionada con su compra */
  119: (componentData: AnalyticsComponentEvent, context?: EcommerceContext): string | undefined => {
    // TODO: Obtener valor --> '-PST' dentro de CD120 se envia aplazado, si no undefined.
    return undefined;
  },

  /** Identificador del almacén o tienda que despachará el producto. Aplica en los casos que se conozca el origen del stock */
  120: (componentData: AnalyticsComponentEvent, context?: EcommerceContext): string | undefined => {
    // TODO: Obtener valor desde peticion cart items stockLocations - stockSourceCode
    return undefined;
  },

  /** Familia comercial a la que pertenece el producto (familyCode_familyName). Breadcrumbs de parrilla en la que esta el producto */
  121: (componentData: AnalyticsComponentEvent, context?: EcommerceContext): string | undefined => {
    return undefined;
  },

  /**  Subfamilia comercial a la que pertenece el producto (subfamilyCode_subfamilyName) */
  123: (componentData: AnalyticsComponentEvent, context?: EcommerceContext): string | undefined => {
    return undefined;
  },

  /** Tipo de producto que se ha visualizado, dentro de la clasificación: product, mocaco, estilismo, bundle, grucaco */
  134: (componentData: AnalyticsComponentEvent, context?: EcommerceContext): string | undefined => {
    // TODO: almacenar en el local storage el tipo y carpeta de estilismo del producto añadido a la cesta para tenerlo en esos eventos
    if (isDefined(componentData.products) && isDefined(componentData.productIndex)) {
      const product = componentData.products[componentData.productIndex];
      return product.gridElemType.toLowerCase() === 'estilismo' && product.color && product.color !== product.colors[0].id
        ? 'product' : product.gridElemType.toLowerCase();
    }
    return undefined;
  },

  /** Indica si el usuario utilizó un click en texto, foto, gif, video… para acceder al detalle del producto */
  144: (componentData: AnalyticsComponentEvent, context?: EcommerceContext): string | undefined => {
    return componentData?.elementType;
  },

  /** Carpeta del set de fotos correspondientes al estilismo. Si no es estilismo se envía el string 'no' */
  190: (componentData: AnalyticsComponentEvent, context?: EcommerceContext): string | undefined => {
    // TODO: almacenar en el local storage el tipo y carpeta de estilismo del producto añadido a la cesta para tenerlo en esos eventos
    if (isDefined(componentData.products) && isDefined(componentData.productIndex)) {
      const productStyle = componentData.products[componentData.productIndex].metadata.stylePath;
      return productStyle && productStyle.length > 0 ? productStyle[0] : 'no';
    }
    return undefined;
  },

  /** Si es el primer producto que se añade a la cesta '1', si no '0' */
  getMetric4: (componentData: AnalyticsComponentEvent, context?: EcommerceContext): string | undefined => {
    // TODO: Obtener valor
    return '0';
  },

  /** Posición del producto dentro de la parrilla o carrusel donde se muestre */
  getMetric5: (componentData: AnalyticsComponentEvent, context?: EcommerceContext): string | undefined => {
    if (isDefined(componentData.products) && isDefined(componentData.productIndex)) {
      const product = componentData.products[componentData.productIndex];
      return product.position?.toString();
    }
    return undefined;
  }
};
